import cn from "classnames";
import { generateUuid } from "helpers/uuid";
import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";

const COMMON_PADDING = 5;

const PLACMENT_ENUM = {
    LEFT: "left",
    RIGHT: "right",
};

const DropdownContext = createContext({});
const DropdownProvider = ({ children, value }) => {
    return <DropdownContext.Provider value={value}>{children}</DropdownContext.Provider>;
};
const useDropdown = () => {
    const contextValue = useContext(DropdownContext);

    return contextValue;
};

const DropdownListContext = createContext({});
const DropdownListProvider = ({ children, value }) => {
    return <DropdownListContext.Provider value={value}>{children}</DropdownListContext.Provider>;
};
const useDropdownList = () => {
    const contextValue = useContext(DropdownListContext);

    return contextValue;
};

const getCountDepthElementsByType = (children, elementType, currentDepth = 0) => {
    return React.Children.toArray(children).reduce((maxDepth, child) => {
        if (React.isValidElement(child)) {
            const newDepth = child.type === elementType ? currentDepth + 1 : currentDepth;
            const childDepth = child.props?.children
                ? getCountDepthElementsByType(child.props.children, elementType, newDepth)
                : newDepth;

            return Math.max(maxDepth, childDepth);
        }
        return maxDepth;
    }, currentDepth);
};

const Dropdown = (props) => {
    const { children, style, innerRef, setOpened } = props;

    const [placement, setPlacement] = useState(PLACMENT_ENUM.RIGHT);

    useLayoutEffect(() => {
        const documentWidth = window.innerWidth;

        const maxDepthList = getCountDepthElementsByType(children, List);

        const dropdownElement = innerRef.current;
        if (!dropdownElement) {
            return;
        }

        const dropdownRect = dropdownElement.getBoundingClientRect();

        const newPlacmentValue =
            style.left + dropdownRect.width * maxDepthList + COMMON_PADDING > documentWidth
                ? PLACMENT_ENUM.LEFT
                : PLACMENT_ENUM.RIGHT;

        setPlacement(newPlacmentValue);
    }, [children, innerRef, style.left]);

    const close = () => setOpened(false);

    const contextValue = useMemo(() => ({ placement, close }), [placement]);

    return (
        <div ref={innerRef} className="popup-dropdown" style={style}>
            <DropdownProvider value={contextValue}>{children}</DropdownProvider>
        </div>
    );
};

const List = ({ children, parentRef }) => {
    const { placement } = useDropdown();

    const currentRef = useRef();

    const [location, setLocation] = useState({});
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [animationStyles, setAnimationStyles] = useState({
        opacity: 0,
        transform: "translateY(10px)",
        transitionProperty: "opacity, transform",
        transitionDuration: ".1s",
        transitionTimingFunction: "ease-out",
    });

    const contextValue = useMemo(() => ({ selectedItemId, setSelectedItemId }), [selectedItemId]);

    // самый первый List
    const isMainList = !parentRef?.current;

    useLayoutEffect(() => {
        setAnimationStyles((prev) => ({ ...prev, opacity: 1, transform: null }));

        if (isMainList) {
            return;
        }

        if (!parentRef.current || !currentRef.current) {
            return;
        }

        const documentHight = window.innerHeight;

        const parentRect = parentRef.current.getBoundingClientRect();
        const currentRect = currentRef.current.getBoundingClientRect();

        let x;
        let y;

        if (placement === PLACMENT_ENUM.RIGHT) {
            x = parentRect.x + parentRect.width;
        } else {
            x = parentRect.x - parentRect.width;
        }

        if (currentRect.y + currentRect.height + COMMON_PADDING > documentHight) {
            y = documentHight - currentRect.height - COMMON_PADDING;
        } else {
            y = parentRect.y;
        }

        setLocation({ x, y });
    }, [isMainList, parentRef, placement]);

    if (isMainList) {
        return (
            <div className="popup-dropdown__list" style={animationStyles}>
                <DropdownListProvider value={contextValue}>{children}</DropdownListProvider>
            </div>
        );
    }

    const style = {
        position: "fixed",
        top: location.y || 0,
        left: location.x || 0,
        zIndex: 999,
        ...animationStyles,
    };

    return (
        <div ref={currentRef} className="popup-dropdown__list" style={style}>
            <DropdownListProvider value={contextValue}>{children}</DropdownListProvider>
        </div>
    );
};

const Item = ({ children, onClick, disabled }) => {
    const ref = useRef();

    const [uuid] = useState(() => generateUuid());

    const { placement, close } = useDropdown();
    const { selectedItemId, setSelectedItemId } = useDropdownList();

    const selected = selectedItemId === uuid;

    const childrenToArray = React.Children.toArray(children);

    const itemComponent = childrenToArray.filter((child) => child.type !== List);
    const listComponent = childrenToArray.filter((child) => child.type === List)?.[0];

    const hasListComponent = !!listComponent;

    const handleClick = () => {
        if (disabled) {
            return;
        }

        if (hasListComponent) {
            // если равен текущему, значит нажали на тот же item, то закрываем (устанавиваем в null)
            setSelectedItemId((prev) => (prev === uuid ? null : uuid));
        } else {
            onClick?.();
            close();
        }
    };

    if (hasListComponent) {
        return (
            <>
                <div
                    ref={ref}
                    className={cn("popup-dropdown__item", {
                        "popup-dropdown__item--disabled": disabled,
                        "popup-dropdown__item--selected": selected,
                        "popup-dropdown__item--left-arrow": placement === PLACMENT_ENUM.LEFT,
                        "popup-dropdown__item--right-arrow": placement === PLACMENT_ENUM.RIGHT,
                    })}
                    onClick={handleClick}
                >
                    {itemComponent}
                </div>
                {selected && React.cloneElement(listComponent, { parentRef: ref })}
            </>
        );
    }

    return (
        <div
            onClick={handleClick}
            className={cn("popup-dropdown__item", { "popup-dropdown__item--disabled": disabled })}
        >
            {children}
        </div>
    );
};

Dropdown.List = List;
Dropdown.Item = Item;

export default Dropdown;
